import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { create } from 'zustand'
import { useSessionStore } from '@/stores'
import { useCartStore } from '@stores/useCartStore'
import { useToast } from '@/hooks/useToast'
import { isInteractivePreviewMode } from '@/utils/previewMode'
import { SectionKey } from '@/constants'
import type { CrossSellProduct } from '@/types'
import { CrossSellNoticeKind, type CrossSellCartNotice } from '../components/OmpCrossSellAddedToast'
import { useOmpActions } from '../ompActions'
import { cartLineTotal, isCrossSellLine, quantityInCart, toCartLine } from '../utils/crossSell'
import { getPageSections } from '../utils/sectionsConfig'
import { usePortalStep } from './usePortalStep'
import { pageFromPath } from '../utils/portalNav'

// The cross-sell cart is shared by the section (cards, View All, Details) and the header's cart
// button, and drawn once by OmpCrossSellCartHost, so its UI state lives here rather than in the section.
interface CrossSellCartUiState {
  cartOpen: boolean
  // A cart change is syncing; every add / remove / quantity control waits for it.
  busy: boolean
  // Offer whose add is syncing, so only its button shows "Adding…".
  addingId: number | null
  notice: CrossSellCartNotice | null
}

export const useCrossSellCartUi = create<CrossSellCartUiState>(() => ({
  cartOpen: false,
  busy: false,
  addingId: null,
  notice: null,
}))

const setUi = (patch: Partial<CrossSellCartUiState>) => useCrossSellCartUi.setState(patch)

export const openCrossSellCart = () => setUi({ notice: null, cartOpen: true })

// The cart store is shared with other flows (Buy Again sets it to one item and leaves it there if
// the customer backs out), so the cross-sell cart is the related-offer lines alone, and each
// cross-sell sync first drops anything else rather than sending it on to checkout.
function dropOtherLines() {
  const { cart, setCart } = useCartStore.getState()
  if (cart.some(line => !isCrossSellLine(line))) {
    setCart(cart.filter(isCrossSellLine))
  }
}

export function useCrossSellCart() {
  const sessionId = useSessionStore(s => (s.session as any)?.id) as number | undefined
  const fullCart = useCartStore(s => s.cart)
  const cart = useMemo(() => fullCart.filter(isCrossSellLine), [fullCart])
  const { cartOpen, busy, addingId, notice } = useCrossSellCartUi()
  const { openCartCheckout } = useOmpActions()
  const toast = useToast()

  // Every change goes through the session cart: the backend prices related lines and drops any
  // whose offer has gone (the store handles that 400 and re-syncs). A failed sync puts the local
  // cart back, so it never shows (or later sends) a change the server didn't take.
  const syncCart = async (change: () => Promise<unknown>): Promise<boolean> => {
    if (!sessionId || useCrossSellCartUi.getState().busy) {
      return false
    }
    setUi({ busy: true })
    dropOtherLines()
    const before = useCartStore.getState().cart
    try {
      await change()
      return true
    } catch {
      useCartStore.getState().setCart(before)
      toast.error('Could not update your cart')
      return false
    } finally {
      setUi({ busy: false })
    }
  }

  // addToCart merges a repeat add of the same offer into its existing line (product + variant +
  // offer), like the marketplace; addItemsToSessionCart would start a new line every time.
  const addLine = (product: CrossSellProduct, quantity: number) => {
    useCartStore.getState().addToCart(toCartLine(product, quantity))
    return useCartStore.getState().updateSessionCart(sessionId!)
  }

  // The button shows "Adding…" until the cart sync answers; the notification follows a successful
  // first add only — a repeat add of a product already in the cart just grows its line quietly.
  const add = async (product: CrossSellProduct, quantity = 1) => {
    if (!sessionId || useCrossSellCartUi.getState().busy) {
      return false
    }
    const alreadyInCart = quantityInCart(useCartStore.getState().cart, product.id) > 0
    setUi({ addingId: product.id })
    const added = await syncCart(() => addLine(product, quantity))
    setUi({ addingId: null })
    if (added && !alreadyInCart) {
      setUi({
        notice: {
          kind: CrossSellNoticeKind.Added,
          name: product.name,
          quantity,
          total: product.price * quantity,
          currency: product.currency,
          imageSource: product.campaign_product,
          stamp: Date.now(),
        },
      })
    }
    return added
  }

  const startCheckout = () => {
    // The editor's preview has no real session to check out.
    if (!isInteractivePreviewMode()) {
      openCartCheckout()
    }
  }

  const buyNow = async (product: CrossSellProduct, quantity: number) => {
    const added = await syncCart(() => addLine(product, quantity))
    if (added) {
      startCheckout()
    }
    return added
  }

  const changeQuantity = (index: number, quantity: number) =>
    syncCart(() => {
      useCartStore.getState().updateCartItem(index, { quantity })
      return useCartStore.getState().updateSessionCart(sessionId!)
    })

  // Like an add, the notification follows the cart sync (the drawer's controls are disabled meanwhile).
  // `index` is into the cross-sell cart, which is the whole cart once syncCart has dropped the rest.
  const remove = async (index: number) => {
    const item = cart[index]
    const removed = await syncCart(() => {
      useCartStore.getState().removeFromCart(index)
      return useCartStore.getState().updateSessionCart(sessionId!)
    })
    if (removed && item) {
      setUi({
        // An empty cart has nothing to review or check out.
        ...(useCartStore.getState().cart.some(isCrossSellLine) ? {} : { cartOpen: false }),
        notice: {
          kind: CrossSellNoticeKind.Removed,
          name: item.variant?.name || item.campaign_product.name,
          quantity: item.quantity,
          total: cartLineTotal(item).price,
          currency: item.campaign_product.currency,
          imageSource: item.campaign_product,
          stamp: Date.now(),
        },
      })
    }
    return removed
  }

  const closeCart = useCallback(() => setUi({ cartOpen: false }), [])
  const closeNotice = useCallback(() => setUi({ notice: null }), [])

  const checkout = () => {
    setUi({ cartOpen: false })
    dropOtherLines()
    startCheckout()
  }

  return {
    cart,
    cartOpen,
    busy,
    addingId,
    notice,
    add,
    buyNow,
    changeQuantity,
    remove,
    openCart: openCrossSellCart,
    closeCart,
    closeNotice,
    checkout,
  }
}

// The header's cart button shows only once something is in the cart, and only on a page whose
// current moment has a visible cross-sell section — the cart exists for cross-sell alone.
export function useCrossSellCartVisible(): boolean {
  const location = useLocation()
  const hasItems = useCartStore(s => s.cart.some(isCrossSellLine))
  const portalSettings = useSessionStore(s => (s.campaign as any)?.portal_settings)
  const page = pageFromPath(location.pathname)
  const { moment } = usePortalStep()
  if (!hasItems) {
    return false
  }
  const sections = getPageSections(portalSettings, page, moment)
  return sections.some(section => section.key === SectionKey.CrossSellCarousel && section.is_visible !== false)
}
